import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { subscribeToPush, unsubscribeFromPush } from '../../lib/pushNotifications';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';

const ManagerNotifications = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [pushStatus, setPushStatus] = useState('unknown');

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) { navigate('/login-authentication'); return; }
      setUserId(user?.id);

      const { data: profile } = await supabase?.from('users')?.select('*')?.eq('id', user?.id)?.single();
      setManagerProfile(profile);

      await fetchNotifications(user?.id);

      // Check actual push status: browser permission + active subscription in DB
      if ('Notification' in window) {
        const browserPermission = Notification.permission;
        if (browserPermission === 'denied') {
          setPushStatus('denied');
        } else {
          // Check if there's an active subscription in DB for this user
          const { data: subs } = await supabase?.from('push_subscriptions')?.select('id')?.eq('user_id', user?.id)?.limit(1);
          setPushStatus(subs && subs.length > 0 ? 'granted' : 'default');
        }
      }
    };
    init();
  }, []);

  const handleEnablePush = async () => {
    setPushStatus('subscribing');
    const success = await subscribeToPush();
    setPushStatus(success ? 'granted' : 'denied');
  };

  const handleDisablePush = async () => {
    setPushStatus('unsubscribing');
    const success = await unsubscribeFromPush();
    setPushStatus(success ? 'default' : 'granted');
  };

  const fetchNotifications = async (uid) => {
    setLoading(true);
    const { data } = await supabase?.from('notifications')?.select('*')?.eq('target_manager_id', uid)?.order('created_at', { ascending: false });
    setNotifications(data || []);
    setLoading(false);
  };

  const markAsRead = async (id) => {
    await supabase?.from('notifications')?.update({ read_at: new Date()?.toISOString() })?.eq('id', id);
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, read_at: new Date()?.toISOString() } : n));
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    await supabase?.from('notifications')?.update({ read_at: new Date()?.toISOString() })?.eq('target_manager_id', userId)?.is('read_at', null);
    setNotifications(prev => prev?.map(n => ({ ...n, read_at: n?.read_at || new Date()?.toISOString() })));
  };

  const getRelativeTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr)?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return t('time.minutesAgo', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('time.hoursAgo', { count: hrs });
    const days = Math.floor(hrs / 24);
    if (days === 1) return t('time.yesterday');
    return t('time.daysAgo', { count: days });
  };

  const getTypeIcon = (type) => {
    const icons = { tier_rankup: '🎉', tier_lost: '⚠️', inactive_days: '😴', activeness_close: '⚡', contract_expiring: '📋', injection_done: '✅', never_live: '🔴', manual: '📢' };
    return icons?.[type] || '🔔';
  };

  const unreadCount = notifications?.filter(n => !n?.read_at)?.length;

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} managerName={managerProfile?.name} />
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <ManagerMobileHeader title="Notifications" />
        <div className="max-w-3xl mx-auto px-3 md:px-6 py-4 md:py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1
                className="font-bold text-white mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(18px, 4vw, 24px)' }}
              >
                {t('notifications.title')}
              </h1>
              {unreadCount > 0 && (
                <p className="text-xs text-white/40" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('notifications.unread', { count: unreadCount })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {pushStatus !== 'granted' && (
                <button
                  onClick={handleEnablePush}
                  disabled={pushStatus === 'subscribing' || pushStatus === 'denied'}
                  className="px-3 py-2 rounded-lg text-xs font-bold transition-all"
                  style={{
                    backgroundColor: pushStatus === 'denied' ? '#1f1f1f' : '#E5202A18',
                    color: pushStatus === 'denied' ? 'rgba(255,255,255,0.3)' : '#E5202A',
                    border: `1px solid ${pushStatus === 'denied' ? '#2a2a2a' : '#E5202A30'}`,
                    fontFamily: 'Sora, sans-serif',
                    minHeight: '44px',
                    opacity: pushStatus === 'subscribing' ? 0.6 : 1,
                  }}
                >
                  {pushStatus === 'subscribing' ? '...' : pushStatus === 'denied' ? t('notifications.pushDenied') : t('notifications.enablePush')}
                </button>
              )}
              {pushStatus === 'granted' && (
                <button
                  onClick={handleDisablePush}
                  className="px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2"
                  style={{ backgroundColor: '#22c55e18', color: '#22c55e', border: '1px solid #22c55e30', fontFamily: 'Sora, sans-serif', minHeight: '44px', cursor: 'pointer' }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5a4 4 0 00-4 4v2.5L1.5 10h11L11 8V5.5a4 4 0 00-4-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M5.5 10.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.3" /></svg>
                  {t('notifications.pushActive')}
                </button>
              )}
              {pushStatus === 'unsubscribing' && (
                <span className="px-3 py-2 rounded-lg text-xs font-bold"
                  style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.3)', border: '1px solid #2a2a2a', fontFamily: 'Sora, sans-serif', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                  ...
                </span>
              )}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 rounded-lg text-sm text-white/70 border hover:text-white transition-colors"
                style={{
                  backgroundColor: '#111111',
                  borderColor: '#1f1f1f',
                  fontFamily: 'Inter, sans-serif',
                  minHeight: '44px',
                }}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 })?.map((_, i) => (
                <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: '#111111' }} />
              ))}
            </div>
          ) : notifications?.length === 0 ? (
            <div className="text-center py-16 text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>
              <p className="text-4xl mb-3">🔔</p>
              <p>{t('notifications.noNotifications')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications?.map(notif => (
                <div
                  key={notif?.id}
                  onClick={() => {
                    if (!notif?.read_at) markAsRead(notif?.id);
                    if (notif?.link) navigate(notif.link);
                  }}
                  className="rounded-xl p-4 cursor-pointer transition-all duration-150"
                  style={{
                    backgroundColor: notif?.read_at ? '#0d0d0d' : '#111111',
                    borderLeft: `4px solid ${notif?.read_at ? '#1f1f1f' : '#E5202A'}`,
                    border: `1px solid ${notif?.read_at ? '#1a1a1a' : '#1f1f1f'}`,
                    borderLeftWidth: '4px',
                    borderLeftColor: notif?.read_at ? '#1f1f1f' : '#E5202A',
                  }}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0 mt-0.5">{getTypeIcon(notif?.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-sm font-bold text-white truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{notif?.title}</p>
                        <span className="text-xs text-white/30 flex-shrink-0" style={{ fontFamily: 'Inter, sans-serif' }}>{getRelativeTime(notif?.created_at)}</span>
                      </div>
                      <p className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{notif?.body}</p>
                    </div>
                    {!notif?.read_at && <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: 'var(--color-primary)' }} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <ManagerBottomNav />
    </div>
  );
};

export default ManagerNotifications;
